'use client';
import { useEffect, useRef, useState } from 'react';

import { useTranslation } from '@lichtblick/i18next';
import { Close, SearchRounded } from '@lichtblick/icons';
import { Colors, TextVariants } from '@lichtblick/theme';
import { ApprovalState, WorkOrderType } from '@lichtblick/types';

import { Input, Options, OptionsContainer, SearchContainer, SearchWrapper, Spacer } from './Search.styles';
import { SearchCard } from './SearchCard/SearchCard';

import { Chip } from '../Chip';
import { IconButton } from '../IconButton';
import { Text } from '../Text';

type ResultsType = {
  clientAddress: string;
  clientName: string;
  id: string;
  orderName: string;
  status: ApprovalState;
};

type SearchProps = {
  onChange: (value: string) => void;
  placeholder?: string;
  results?: ResultsType[];
  value?: string | number;
};

const SearchOptions = ({ onChange }: { onChange: (value: string) => void }) => {
  const { t } = useTranslation(['common', 'glossary']);

  return (
    <div data-testid="search-options">
      <Text $variant={TextVariants.title.m} color={Colors.blue[100]}>
        {t('common:workOrders')}
      </Text>
      <Options>
        {Object.values(WorkOrderType).map((value) => (
          <Chip
            key={value}
            onClick={() => onChange(t(`glossary:workOrderTypes:${value}`))}
            shouldShowIcon={false}
            size="large"
            text={t(`glossary:workOrderTypes:${value}`)}
            variant="descriptive-lightblue"
          />
        ))}
      </Options>
      <Spacer />
      <Text $variant={TextVariants.title.m} color={Colors.blue[100]}>
        {t('common:orderStatus')}
      </Text>
      <Options>
        {Object.values(ApprovalState).map((value) => (
          <Chip key={value} onClick={() => onChange(t(`common:workStatus:${value}`))} size="large" variant={value} />
        ))}
      </Options>
    </div>
  );
};

export const Search = ({ onChange, placeholder, results, value }: SearchProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close the dropdown if the click is outside the search container
      if (searchContainerRef.current && !searchContainerRef.current.contains(event?.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchContainerRef]);

  return (
    <SearchWrapper ref={searchContainerRef}>
      <>
        {isOpen ? (
          <SearchContainer>
            <Input
              onChange={(e) => onChange(e.target.value)}
              onFocus={() => setIsOpen(true)}
              placeholder={placeholder}
              value={value}
            />
            <Close onClick={() => onChange('')} />
          </SearchContainer>
        ) : (
          <IconButton
            data-testid="search-button"
            icon={<SearchRounded sx={{ color: Colors.blue['100'] }} />}
            onClick={() => setIsOpen(true)}
            variant="ghost"
          />
        )}
      </>

      {isOpen && (
        <OptionsContainer data-testid="options-container">
          {results?.length ? (
            <>
              {results.map((data) => (
                <SearchCard data={data} key={data.id} />
              ))}
            </>
          ) : (
            <SearchOptions onChange={onChange} />
          )}
        </OptionsContainer>
      )}
    </SearchWrapper>
  );
};
